import { useCallback, useMemo, useState } from "react";

import { ROWS_PER_PAGE } from "@covertree/consts";
import { useTableViewPagination } from "@covertree/hooks";

import {
  GetPolicyListItemsQueryVariables,
  PolicyFilterByStatus,
  PolicyListItemsSortBy,
  SortOrder,
} from "@covertree/agent-portal-graphql";
import { useGetPolicyListItemsQuery } from "@hooks/policies/useGetPolicyListItems";
import { useSendEsignReminder } from "@hooks/actions/useSendEsignReminder";

import { useMappedPolicies } from "./useMappedPolicies";

const filters = [
  {
    label: PolicyFilterByStatus.All,
    value: PolicyFilterByStatus.All,
  },
  {
    label: PolicyFilterByStatus.Active,
    value: PolicyFilterByStatus.Active,
  },
  {
    label: "Pending E-Sign",
    value: PolicyFilterByStatus.PendingESign,
  },
  {
    label: PolicyFilterByStatus.Cancelled,
    value: PolicyFilterByStatus.Cancelled,
  },
  {
    label: PolicyFilterByStatus.Expired,
    value: PolicyFilterByStatus.Expired,
  },
];

const initialPagination = {
  from: "0",
  filter: filters[0].value,
  order: SortOrder.Descending,
  sortBy: PolicyListItemsSortBy.IssuedDate,
};

const columns = [
  { headerName: "Policy ID" },
  { headerName: "Name" },
  { headerName: "Address" },
  { headerName: "Type" },
  { headerName: "Premium", sortBy: PolicyListItemsSortBy.Premium },
  { headerName: "Status" },
  {
    headerName: "Issue Date",
    sortBy: PolicyListItemsSortBy.IssuedDate,
  },
  {
    headerName: "Effective Date",
    sortBy: PolicyListItemsSortBy.EffectiveDate,
  },
  { headerName: "Assigned Agent" },
  { headerName: "Assigned Agency" },
];

export const useGetPolicies = (showSequoiaOnly: boolean) => {
  const [rowsPerPage, setRowsPerPage] = useState<number>(ROWS_PER_PAGE);

  const { sendEsignReminder, loading: isSendEsignReminderLoading } =
    useSendEsignReminder();

  const {
    from,
    order,
    sortBy,
    filter,
    currentPage,
    onChangePage,
    onChangeFilter,
    onChangeSort,
  } = useTableViewPagination(0, rowsPerPage, initialPagination);

  const handleSortChange = useCallback(
    (nextSortBy: string) => () => {
      onChangeSort(nextSortBy);
    },
    [onChangeSort]
  );

  const queryVariables: GetPolicyListItemsQueryVariables = useMemo(
    () => ({
      pagination: {
        from,
        size: rowsPerPage,
      },
      sorting: {
        sort: order,
        sortBy: sortBy as PolicyListItemsSortBy,
      },
      filter: {
        status: filter as PolicyFilterByStatus,
        showSequoiaOnly,
      },
    }),
    [filter, from, order, showSequoiaOnly, sortBy, rowsPerPage]
  );

  const {
    policies: items,
    totalCount,
    loading,
    error,
  } = useGetPolicyListItemsQuery(queryVariables);

  const policies = useMappedPolicies(items || [], sendEsignReminder);

  return {
    policies,
    loading,
    isAnyActionLoading: isSendEsignReminderLoading,
    error: !!error,
    columns,
    filters,
    order,
    sortBy,
    currentPage,
    rowsPerPage,
    totalCount,
    handleSortChange,
    handleChangePage: onChangePage,
    handleFilterChange: onChangeFilter,
    handleRowsPerPageChange: setRowsPerPage,
  };
};
